import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * "Take your WeNext workspace with you" mobile-app card — App Store + Google
 * Play badges and a QR. Ported pixel-for-pixel from the onboarding design
 * (wenext-onboarding). Self-contained SVGs, no external deps.
 *
 * Optional store URLs — pass real links when available; the badges fall back to
 * "#" so nothing 404s if a store isn't live yet. When `qrUrl` is given, a real
 * scannable QR is rendered (via qrcode.react); otherwise a decorative QR shows.
 */
interface MobileAppCardProps {
  appStoreUrl?: string;
  googlePlayUrl?: string;
  /** Target the QR encodes. Omit to show the decorative placeholder QR. */
  qrUrl?: string;
}

/** Deterministic decorative QR (no data encoded) — matches the design mock. */
function FakeQR() {
  const cells = 21;
  const rand = (r: number, c: number) => (r * 31 + c * 17 + r * c) % 3 === 0;
  const rows: ReactNode[] = [];
  for (let r = 0; r < cells; r++) {
    const row: ReactNode[] = [];
    for (let c = 0; c < cells; c++) {
      const inCorner =
        (r < 7 && c < 7) || (r < 7 && c >= cells - 7) || (r >= cells - 7 && c < 7);
      const fill = inCorner || rand(r, c);
      row.push(
        <rect key={c} x={c * 8} y={r * 8} width={7} height={7} rx={1.5} fill={fill ? '#101828' : 'transparent'} />,
      );
    }
    rows.push(<g key={r}>{row}</g>);
  }
  const finder = (x: number, y: number) => (
    <g key={`${x}-${y}`}>
      <rect x={x} y={y} width={56} height={56} rx={10} fill="#101828" />
      <rect x={x + 8} y={y + 8} width={40} height={40} rx={6} fill="#FFFFFF" />
      <rect x={x + 16} y={y + 16} width={24} height={24} rx={4} fill="#101828" />
    </g>
  );
  return (
    <svg viewBox="0 0 168 168" className="hidden h-32 w-32 shrink-0 sm:h-36 sm:w-36 lg:block">
      <rect width="168" height="168" rx="16" fill="#FFFFFF" />
      <g transform="translate(4 4)">
        {rows}
        {finder(0, 0)}
        {finder(cells * 8 - 56, 0)}
        {finder(0, cells * 8 - 56)}
      </g>
      <g transform="translate(60 60)">
        <rect width="48" height="48" rx="12" fill="#0EB64F" />
        <g transform="translate(24 24)" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" fill="none">
          <path d="M -8 0 L 8 0" />
          <path d="M 0 -8 L 0 8" />
        </g>
      </g>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.3 12.5c0-2.4 2-3.5 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.6.9-.8 0-1.9-.9-3.2-.8-1.6.1-3.2 1-4 2.5-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.4 1.3 0 1.7-.8 3.2-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.5-1-2.6-3.9zM14.9 5.3c.7-.8 1.1-2 1-3.1-1 .1-2.1.6-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M4 2.2v19.6c0 .8.9 1.3 1.6.9l14-9.8c.7-.5.7-1.4 0-1.9l-14-9.8C4.9.9 4 1.4 4 2.2z" />
    </svg>
  );
}

export function MobileAppCard({ appStoreUrl = '#', googlePlayUrl = '#', qrUrl }: MobileAppCardProps) {
  return (
    <div className="rounded-2xl bg-white border border-[#E4E7EC] p-5 sm:p-6 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div className="flex items-center gap-2 mb-1">
        <span className="rounded-full bg-[#E7F7EC] text-[#076A2D] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide">
          Mobile app
        </span>
        <span className="text-[12px] text-[#6B7280]">iOS &amp; Android</span>
      </div>

      {/* `md:items-end` bottom-aligns the QR with the store badges — the badges
          are the last thing in the left column, so a shared bottom edge is what
          makes the two read as one row. Centred (the old value) left the QR
          floating against the description with its bottom hanging past the
          badges by whatever the height difference happened to be. */}
      <div className="flex flex-col md:flex-row items-start md:items-end gap-5 md:gap-6 mt-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-[20px] font-semibold leading-[1.15] tracking-[-0.01em] text-[#101828] sm:text-[24px]">
            Take your WeNext workspace with you
          </h3>
          <p className="mt-2 text-[13.5px] leading-relaxed text-[#475467]">
            Reply to conversations, get push notifications for every new lead, and trigger flows
            from your phone — grab it from the stores.
          </p>

          {/* Mobile: equal `flex-1` halves fill the card so neither badge
              leaves a gap. Desktop (`md+`): hug content — stretching to the
              full column made each badge ~376px of empty navy. */}
          <div className="mt-4 flex w-full gap-2 sm:gap-2.5 md:w-auto">
            <a
              href={appStoreUrl}
              target={appStoreUrl === '#' ? undefined : '_blank'}
              rel={appStoreUrl === '#' ? undefined : 'noreferrer'}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-[#101828] px-2.5 py-2 text-white transition hover:bg-[#0F172A] sm:gap-2.5 sm:px-3.5 md:w-auto md:flex-none"
            >
              <span className="h-6 w-6 shrink-0 text-white">
                <AppleIcon />
              </span>
              <span className="min-w-0 text-left leading-tight">
                <span className="block text-[9px] uppercase tracking-wider text-white/70 sm:text-[10px]">
                  Download on the
                </span>
                <span className="block truncate text-[13px] font-semibold sm:text-[14px]">
                  App Store
                </span>
              </span>
            </a>
            <a
              href={googlePlayUrl}
              target={googlePlayUrl === '#' ? undefined : '_blank'}
              rel={googlePlayUrl === '#' ? undefined : 'noreferrer'}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-[#101828] px-2.5 py-2 text-white transition hover:bg-[#0F172A] sm:gap-2.5 sm:px-3.5 md:w-auto md:flex-none"
            >
              <span className="h-6 w-6 shrink-0">
                <PlayIcon />
              </span>
              <span className="min-w-0 text-left leading-tight">
                <span className="block text-[9px] uppercase tracking-wider text-white/70 sm:text-[10px]">
                  Get it on
                </span>
                <span className="block truncate text-[13px] font-semibold sm:text-[14px]">
                  Google Play
                </span>
              </span>
            </a>
          </div>
        </div>

        {qrUrl ? (
          <div className="hidden shrink-0 rounded-2xl bg-white p-3 ring-1 ring-[#E4E7EC] shadow-[0_1px_2px_rgba(16,24,40,0.04)] lg:block">
            <QRCodeSVG
              value={qrUrl}
              size={124}
              bgColor="#FFFFFF"
              fgColor="#101828"
              level="M"
              marginSize={0}
              className="h-[124px] w-[124px] sm:h-[132px] sm:w-[132px]"
            />
          </div>
        ) : (
          <FakeQR />
        )}
      </div>
    </div>
  );
}
